import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "messageField", "recipientIds", "submitButton", "charCount"]

  static values = {
    recipientType: String,
    successMessage: { type: String, default: "An SMS was successfully sent!" },
    errorMessage: { type: String, default: "Failed to send SMS. Please try again." },
    maxLength: { type: Number, default: 160 },
    recipientField: { type: String, default: "recipient_ids[]" }
  }

  connect() {
    this.setupBoundHandlers()
    this.setupFormListeners()
    this.setupMessageField()
  }

  disconnect() {
    if (this.hasFormTarget) {
      this.formTarget.removeEventListener("ajax:before", this.boundBeforeSubmit)
      this.formTarget.removeEventListener("ajax:success", this.boundHandleSuccess)
      this.formTarget.removeEventListener("ajax:error", this.boundHandleError)
      this.formTarget.removeEventListener("ajax:complete", this.boundHandleComplete)
    }

    if (this.hasMessageFieldTarget) {
      this.messageFieldTarget.removeEventListener("input", this.boundHandleInput)
    }
  }

  setupBoundHandlers() {
    this.boundBeforeSubmit = this.beforeSubmit.bind(this)
    this.boundHandleSuccess = this.handleSuccess.bind(this)
    this.boundHandleError = this.handleError.bind(this)
    this.boundHandleComplete = this.handleComplete.bind(this)
    this.boundHandleInput = this.handleInput.bind(this)
  }

  setupFormListeners() {
    if (!this.hasFormTarget) return

    this.formTarget.addEventListener("ajax:before", this.boundBeforeSubmit)
    this.formTarget.addEventListener("ajax:success", this.boundHandleSuccess)
    this.formTarget.addEventListener("ajax:error", this.boundHandleError)
    this.formTarget.addEventListener("ajax:complete", this.boundHandleComplete)
  }

  setupMessageField() {
    if (!this.hasMessageFieldTarget) return

    this.messageFieldTarget.addEventListener("input", this.boundHandleInput)
    this.updateCharCount()
  }

  beforeSubmit(event) {
    if (this.hasMessageFieldTarget) {
      this.messageFieldTarget.value = this.messageFieldTarget.value.trim()
    }

    if (this.isMessageBlank()) {
      if (event && typeof event.preventDefault === "function") {
        event.preventDefault()
      }
      this.showAlert("Validation Error", "warning", "SMS content cannot be empty.")
      return false
    }

    this.enforceMaxLength()
    this.populateRecipientIds()
    this.toggleSubmitting(true)

    return true
  }

  handleSuccess() {
    this.toggleSubmitting(false)
    this.showAlert("Alḥamdulillāh", "success", this.successMessageValue, () => {
      this.closeModal()
      window.location.reload()
    })
  }

  handleError(event) {
    this.toggleSubmitting(false)
    const [data] = event.detail
    const message = data && data.error ? data.error : this.errorMessageValue
    this.showAlert("Error", "error", message)
  }

  handleComplete() {
    this.toggleSubmitting(false)
  }

  handleInput() {
    this.enforceMaxLength()
    this.updateCharCount()
  }

  populateRecipientIds() {
    if (!this.hasFormTarget) return

    const inputName = this.recipientFieldValue || "recipient_ids[]"
    const existingFields = Array.from(this.formTarget.querySelectorAll(`input[name="${inputName}"]`))

    if (typeof batch_action_ids !== "undefined" && Array.isArray(batch_action_ids) && batch_action_ids.length > 0) {
      existingFields.forEach(field => field.remove())

      batch_action_ids.forEach(id => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = inputName
        input.value = id
        this.formTarget.appendChild(input)
      })
    } else if (this.hasRecipientIdsTarget) {
      const recipientId = this.recipientIdsTarget.value || this.recipientIdsTarget.dataset.recipientId

      if (recipientId) {
        existingFields.forEach(field => field.remove())

        const input = document.createElement("input")
        input.type = "hidden"
        input.name = inputName
        input.value = recipientId
        this.formTarget.appendChild(input)
      }
    }
  }

  enforceMaxLength() {
    if (!this.hasMessageFieldTarget) return

    const value = this.messageFieldTarget.value

    if (value.length > this.maxLengthValue) {
      this.messageFieldTarget.value = value.substring(0, this.maxLengthValue)
    }

    this.updateCharCount()
  }

  updateCharCount() {
    if (!this.hasCharCountTarget || !this.hasMessageFieldTarget) return

    const remaining = Math.max(this.maxLengthValue - this.messageFieldTarget.value.length, 0)
    this.charCountTarget.textContent = remaining
  }

  toggleSubmitting(disabled) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = disabled
    }
  }

  isMessageBlank() {
    if (!this.hasMessageFieldTarget) return true
    return this.messageFieldTarget.value.trim().length === 0
  }

  showAlert(title, type, text, callback) {
    if (typeof swal === "function") {
      swal({ title, type, text }, callback)
    } else {
      alert(text)
      if (callback) callback()
    }
  }

  closeModal() {
    const modal = this.element.closest(".modal")
    if (modal) {
      $(modal).modal("hide")
    }
  }
}
